/**
 * The small things lying on the ground.
 *
 * Grass is not a colour, it is grass: tufts of it, with a pebble here and there
 * and the odd stick. Rock has stones on it. A dirt track has twigs and grit
 * worked into it. Without any of that, ground painted one colour reads as a
 * painted plane however good the colour is, which is most of what makes a
 * landscape look empty from head height.
 *
 * Three rules this has to obey, and they decide the whole design.
 *
 * It costs nothing per frame. Everything is built once, with the square of
 * terrain it belongs to, as one instanced mesh per kind: three draw calls for a
 * square, however many thousand tufts are standing in it. Nothing is animated,
 * nothing is sorted, and nothing is looked at again until the square is thrown
 * away.
 *
 * It is the same for everybody. Where a tuft stands is a hash of the tile it
 * stands on, not a random number, so two people looking at the same field see
 * the same field. That also means it survives being rebuilt: editing one tile
 * does not shuffle the grass in the rest of the square.
 *
 * It is not placed by hand. What grows where comes from what the ground is
 * painted, so painting a patch of rock puts stones on it in the same stroke and
 * nobody has to go back and scatter them.
 */

import * as THREE from 'three';
import { GroundKind } from '@inverse/common';

/**
 * How much one square may hold of any one kind.
 *
 * A budget rather than a limit on the shape of the thing: a square of grass at
 * the density below wants five thousand tufts, which is more than is worth
 * drawing, so it is thinned to fit. Thinned evenly, and that is the whole point
 * of the number being here rather than being a break in the middle of a loop.
 * It used to stop adding the moment it was reached, which fills a square in the
 * order its tiles are walked and then stops dead: half a field of grass and half
 * a field of nothing, with a straight edge down the middle of it where the count
 * ran out.
 */
const MOST_PER_SQUARE = 3400;

/*
 * A target rather than a ceiling: the thinning is a roll per thing, so a square
 * lands near the number rather than exactly under it. Near is what matters.
 */

/**
 * A steady hash of a tile and a number.
 *
 * Not a random source: the same tile always answers the same thing, which is
 * what makes the clutter the same for every player and stable across a rebuild.
 */
function rollAt(tx: number, tz: number, salt: number): number {
  let held =
    (Math.imul(tx, 374761393) + Math.imul(tz, 668265263) + Math.imul(salt, 2246822519)) | 0;
  held = Math.imul(held ^ (held >>> 13), 1274126177);
  return ((held ^ (held >>> 16)) >>> 0) / 4294967296;
}

/** What one kind of clutter looks like, and how thickly it lies. */
interface Kind {
  /**
   * What it is, which is not the same as which one this is.
   *
   * Several kinds of ground grow a pebble, and a pebble on snow is not the
   * colour of a pebble on grass. The key says what shape to use; the id below
   * says which entry it is, and everything counted, thinned and drawn is
   * counted, thinned and drawn under the id.
   */
  key: string;
  /** How likely a tile is to carry one, before the roll for how many. */
  chance: number;
  /** At most this many on one tile, and never fewer than one where it lands. */
  most: number;
  colour: number;
  geometry: () => THREE.BufferGeometry;
  /** How much bigger or smaller than its modelled size one may be drawn. */
  vary: number;
  /** Whether it stands up, and so wants turning about its own axis only. */
  upright: boolean;
  /** Which entry this is: the ground it grows on and what it is. Filled below. */
  id: string;
}

/** One entry as it is written, before it is told which ground it belongs to. */
type Written = Omit<Kind, 'id'>;

/** A tuft: three blades leaning apart, which reads as grass from any angle. */
function tuft(): THREE.BufferGeometry {
  const blades: THREE.BufferGeometry[] = [];

  for (let at = 0; at < 3; at++) {
    const blade = new THREE.ConeGeometry(0.035, 0.26, 3);
    blade.translate(0, 0.13, 0);
    blade.rotateZ((at - 1) * 0.36);
    blade.rotateY(at * 2.1);
    blade.translate((at - 1) * 0.05, 0, (at - 1) * 0.04);
    blades.push(blade);
  }

  return join(blades);
}

/** A pebble: a squashed sphere, cheap and round enough at this size. */
function pebble(): THREE.BufferGeometry {
  const held = new THREE.SphereGeometry(0.075, 5, 4);
  held.scale(1, 0.62, 1);
  held.translate(0, 0.04, 0);
  return held;
}

/** A stone: the same idea, bigger and less round. */
function stone(): THREE.BufferGeometry {
  const held = new THREE.DodecahedronGeometry(0.19, 0);
  held.scale(1, 0.72, 0.86);
  held.translate(0, 0.1, 0);
  return held;
}

/** A stick lying flat, because a stick standing up is a post. */
function stick(): THREE.BufferGeometry {
  const held = new THREE.CylinderGeometry(0.026, 0.02, 0.42, 4);
  held.rotateZ(Math.PI / 2);
  held.translate(0, 0.025, 0);
  return held;
}

function join(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  // Merged by hand rather than with the addon, so nothing extra is imported for
  // four cones. Every part here is non indexed and carries the same attributes.
  const positions: number[] = [];
  const normals: number[] = [];

  for (const part of parts) {
    const held = part.index ? part.toNonIndexed() : part;
    positions.push(...Array.from(held.getAttribute('position').array));
    normals.push(...Array.from(held.getAttribute('normal').array));
    if (held !== part) held.dispose();
    part.dispose();
  }

  const whole = new THREE.BufferGeometry();
  whole.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  whole.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  return whole;
}

/**
 * What lies on each kind of ground.
 *
 * A short list on purpose. Every entry is a draw call for every square it
 * appears in, so the difference between three kinds and ten is the difference
 * between clutter and a frame budget.
 */
const WRITTEN: Readonly<Record<number, Written[]>> = {
  [GroundKind.Grass]: [
    {
      key: 'tuft',
      chance: 0.62,
      most: 3,
      colour: 0x4a7a3c,
      geometry: tuft,
      vary: 0.4,
      upright: true,
    },
    {
      key: 'pebble',
      chance: 0.06,
      most: 1,
      colour: 0x7c7469,
      geometry: pebble,
      vary: 0.5,
      upright: false,
    },
  ],
  [GroundKind.Rock]: [
    {
      key: 'pebble',
      chance: 0.4,
      most: 3,
      colour: 0x807a72,
      geometry: pebble,
      vary: 0.6,
      upright: false,
    },
    {
      key: 'stone',
      chance: 0.09,
      most: 1,
      colour: 0x6d675f,
      geometry: stone,
      vary: 0.55,
      upright: false,
    },
  ],
  [GroundKind.Dirt]: [
    {
      key: 'pebble',
      chance: 0.22,
      most: 2,
      colour: 0x8a7d6b,
      geometry: pebble,
      vary: 0.5,
      upright: false,
    },
    {
      key: 'stick',
      chance: 0.09,
      most: 1,
      colour: 0x5b4736,
      geometry: stick,
      vary: 0.4,
      upright: false,
    },
  ],
  [GroundKind.Path]: [
    {
      key: 'pebble',
      chance: 0.3,
      most: 2,
      colour: 0x968a76,
      geometry: pebble,
      vary: 0.4,
      upright: false,
    },
  ],
  // Snow reads best clean: the shape of a peak is the whole of what is up
  // there. It used to have dark sticks lying on it at one tile in twenty, which
  // over a white dome the size of Deepwall came out as a field of dirt, and a
  // fallen branch eighty tiles above the last tree is a thing nobody put there.
  // What is left is the odd stone showing through, in the colour of snow in
  // shadow rather than the colour of earth.
  [GroundKind.Snow]: [
    {
      key: 'pebble',
      chance: 0.035,
      most: 1,
      colour: 0xa9b6c0,
      geometry: pebble,
      vary: 0.45,
      upright: false,
    },
  ],
  // Sand: a beach and a desert both, and both were bare. Sparse, because sand
  // is mostly sand, and pale, because anything dark on it reads as litter. No
  // grass: a tuft growing out of a beach is a tuft nobody believes.
  [GroundKind.Sand]: [
    {
      key: 'pebble',
      chance: 0.075,
      most: 1,
      colour: 0xc0ab84,
      geometry: pebble,
      vary: 0.55,
      upright: false,
    },
  ],
  [GroundKind.Ash]: [
    {
      key: 'pebble',
      chance: 0.24,
      most: 2,
      colour: 0x555049,
      geometry: pebble,
      vary: 0.6,
      upright: false,
    },
    {
      key: 'stone',
      chance: 0.05,
      most: 1,
      colour: 0x413c37,
      geometry: stone,
      vary: 0.5,
      upright: false,
    },
  ],
};

/**
 * The same lists, each entry told which ground it grows on.
 *
 * Without this every pebble in the world was the same pebble: the lists were
 * gathered into one map keyed by what a thing is called, the first pebble found
 * won, and the pale stones meant for snow and the sandy ones meant for a beach
 * were both drawn in the grey chosen for a field.
 */
const GROWS: Readonly<Record<number, Kind[]>> = Object.fromEntries(
  Object.entries(WRITTEN).map(([ground, list]) => [
    Number(ground),
    list.map((kind) => ({ ...kind, id: `${ground}:${kind.key}` })),
  ]),
);

/** Every entry there is, so one mesh per entry can be made and filled. */
const EVERY: Kind[] = Object.values(GROWS).flat();

export interface ClutterGround {
  width: number;
  height: number;
  originX: number;
  originZ: number;
  /** What each tile is painted, however the region happens to hold it. */
  ground?: ArrayLike<number>;
}

/**
 * Everything that lies on one square of ground.
 *
 * Nothing at all where a square has no painted ground, which is most of an
 * ocean: the answer is an empty group, and an empty group costs nothing.
 */
export function clutterFor(
  region: ClutterGround,
  piece: { tx: number; tz: number; width: number; height: number },
  /** How high the ground is at a point, which is the scene's business. */
  heightAt: (x: number, z: number) => number,
  /** Whether a tile is under water, since nothing grows in the sea. */
  wet: (index: number) => boolean,
  /**
   * Whether something has been laid over the ground on a tile.
   *
   * A floor, a deck, a jetty: the ground is still grass or sand underneath and
   * still says so, so grass went on growing through the boards of a dock and
   * pebbles lay about on the floorboards of a house.
   */
  covered: (tx: number, tz: number) => boolean,
): THREE.Group {
  const group = new THREE.Group();
  const filled = new Map<string, THREE.Matrix4[]>();

  const spot = new THREE.Object3D();

  /**
   * Everywhere something of a kind would stand, before any of it is thinned.
   *
   * Walked once to count and again to place, so the thinning can be an even
   * share of the whole square rather than whatever was reached before a budget
   * ran out.
   */
  const wanted = new Map<string, number>();

  for (let dz = 0; dz < piece.height; dz++) {
    for (let dx = 0; dx < piece.width; dx++) {
      const tx = piece.tx + dx;
      const tz = piece.tz + dz;
      const index = tz * region.width + tx;
      if (wet(index)) continue;
      if (covered(tx, tz)) continue;

      const kinds = GROWS[region.ground?.[index] ?? 0];
      if (!kinds) continue;

      for (const kind of kinds) {
        if (rollAt(tx, tz, kind.key.length * 31 + 7) >= kind.chance) continue;

        const many = 1 + Math.floor(rollAt(tx, tz, kind.key.length * 53 + 11) * kind.most);
        wanted.set(kind.id, (wanted.get(kind.id) ?? 0) + many);
      }
    }
  }

  /**
   * What share of each kind survives the budget.
   *
   * One for anything that fits. Anything that does not is thinned by a roll of
   * its own, so what is dropped is scattered through the square instead of being
   * everything past a line across the middle of it.
   */
  const share = new Map<string, number>();
  for (const [key, many] of wanted) {
    share.set(key, many <= MOST_PER_SQUARE ? 1 : MOST_PER_SQUARE / many);
  }

  for (let dz = 0; dz < piece.height; dz++) {
    for (let dx = 0; dx < piece.width; dx++) {
      const tx = piece.tx + dx;
      const tz = piece.tz + dz;
      const index = tz * region.width + tx;
      if (wet(index)) continue;
      if (covered(tx, tz)) continue;

      const kinds = GROWS[region.ground?.[index] ?? 0];
      if (!kinds) continue;

      for (const kind of kinds) {
        if (rollAt(tx, tz, kind.key.length * 31 + 7) >= kind.chance) continue;

        const many = 1 + Math.floor(rollAt(tx, tz, kind.key.length * 53 + 11) * kind.most);
        const held = filled.get(kind.id) ?? [];
        const keeping = share.get(kind.id) ?? 1;

        for (let one = 0; one < many; one++) {
          if (keeping < 1 && rollAt(tx, tz, one * 131 + 23) >= keeping) continue;

          const alongX = rollAt(tx, tz, one * 97 + 3);
          const alongZ = rollAt(tx, tz, one * 89 + 5);
          const turn = rollAt(tx, tz, one * 61 + 13) * Math.PI * 2;
          const size = 1 + (rollAt(tx, tz, one * 43 + 17) - 0.5) * 2 * kind.vary;

          const x = region.originX + tx + 0.12 + alongX * 0.76;
          const z = region.originZ + tz + 0.12 + alongZ * 0.76;

          spot.position.set(x, heightAt(x, z), z);
          spot.rotation.set(0, turn, 0);
          // Anything lying down is tipped a little, so a field of pebbles is not
          // a field of the same pebble.
          if (!kind.upright) spot.rotation.x = (rollAt(tx, tz, one * 71 + 19) - 0.5) * 0.5;
          spot.scale.setScalar(Math.max(0.4, size));
          spot.updateMatrix();

          held.push(spot.matrix.clone());
        }

        filled.set(kind.id, held);
      }
    }
  }

  for (const kind of EVERY) {
    const held = filled.get(kind.id);
    if (!held || held.length === 0) continue;

    const mesh = new THREE.InstancedMesh(
      kind.geometry(),
      new THREE.MeshLambertMaterial({ color: kind.colour, flatShading: true }),
      held.length,
    );

    for (let at = 0; at < held.length; at++) mesh.setMatrixAt(at, held[at] as THREE.Matrix4);
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    // It is small and it is everywhere; nothing about it is worth a pointer.
    mesh.raycast = () => undefined;
    group.add(mesh);
  }

  return group;
}

/** Lets go of everything a square's clutter was holding. */
export function forgetClutter(group: THREE.Group): void {
  for (const child of group.children) {
    if (!(child instanceof THREE.InstancedMesh)) continue;

    (child.geometry as THREE.BufferGeometry).dispose();
    (child.material as THREE.Material).dispose();
    child.dispose();
  }

  group.clear();
}
